import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, CalendarClock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Mandatory "Next Action" popup that appears after a lead status changes to
 * one of the trigger statuses (Qualified Lead, Virtual Meet, Awaited Visit,
 * Awaited LOI, Nurture). Saves a reminder in db.reminders so it shows up in
 * Today's Priorities → Follow-ups counter.
 */
const DEFAULT_ACTION_TYPES = [
  'Call Back', 'Site Visit', 'Collect Token Payment', 'Send Agreement',
  'WhatsApp Follow-up', 'Send Quote/Proposal', 'Schedule Virtual Meet',
];

const NextActionDialog = ({ open, onClose, lead, statusContext, onSaved }) => {
  const { api } = useAuth();
  const [actionTypes, setActionTypes] = useState(DEFAULT_ACTION_TYPES);
  const [actionType, setActionType] = useState('Call Back');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('10:00');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset & fetch live trigger-statuses + action-types from server
    const t = new Date();
    t.setDate(t.getDate() + 1);
    setDueDate(t.toISOString().slice(0, 10));
    setDueTime('10:00');
    setActionType('Call Back');
    setRemarks('');
    api.get('/intel/next-action/trigger-statuses')
      .then(({ data }) => setActionTypes(data.actionTypes || DEFAULT_ACTION_TYPES))
      .catch(() => {});
  }, [open, api]);

  const handleSave = async () => {
    if (!actionType) return toast.error('Pick an action type');
    if (!dueDate) return toast.error('Pick a date');
    if (!dueTime) return toast.error('Pick a time');
    const due = new Date(`${dueDate}T${dueTime}:00`);
    if (isNaN(due.getTime())) return toast.error('Invalid date/time');
    setSaving(true);
    try {
      await api.post(`/intel/leads/${lead.id}/next-action`, {
        actionType,
        dueDateTime: due.toISOString(),
        remarks: remarks.trim(),
      });
      toast.success(`Next action saved · ${actionType} on ${dueDate} ${dueTime}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save next action';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save next action');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="next-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" /> What is the next action?
          </DialogTitle>
          <DialogDescription>
            Every interaction needs a follow-up step.
            {statusContext && (
              <span className="block mt-1 text-xs">
                You just moved <strong>{lead.name}</strong> to <strong>{statusContext}</strong>.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Action Type *</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger data-testid="action-type-select">
                <SelectValue placeholder="Pick an action" />
              </SelectTrigger>
              <SelectContent>
                {actionTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                data-testid="action-date" />
            </div>
            <div>
              <Label>Time *</Label>
              <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                data-testid="action-time" />
            </div>
          </div>
          <div>
            <Label>Remarks</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Call after 3 PM, confirm site address"
              rows={3} data-testid="action-remarks" />
          </div>
          <Alert>
            <CalendarClock className="h-4 w-4" />
            <AlertDescription className="text-xs">
              This will appear in <strong>Today&apos;s Priorities → Follow-ups</strong>
              {' '}when the due date arrives.
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button onClick={handleSave} disabled={saving} className="w-full"
            data-testid="save-next-action-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
            Save Next Action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NextActionDialog;
